import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { useTranslation } from 'react-i18next'

import { servicesApi } from '../../api/endpoints'
import { useLocale } from '../../i18n/LocaleProvider'
import { useFormatters } from '../../hooks/useFormatters'
import { ErrorState } from '../../components/common/States'
import type { ServiceItemDto } from '../../types/api'

/**
 * Two hours, in milliseconds.
 *
 * The earliest the picker offers. Every orderable item carries its own notice in minutes and
 * the server enforces that; this is only so the field does not open on a time that is already
 * impossible for everything on the menu.
 */
const SOONEST_MS = 2 * 60 * 60 * 1000

/** `2026-09-21T18:30`, which is what a datetime-local input wants. */
function localInputValue(date: Date): string {
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return shifted.toISOString().slice(0, 16)
}

/**
 * Ordering breakfast, soup and the rest from the cottage.
 *
 * <p>The API has taken orders since the first release and nothing on the site could place one:
 * a guest read the menu, saw "order online", and had no way to do it. This is that form.
 *
 * <p>Identity is the booking reference and the e-mail used to book, which is how the API
 * recognises a guest everywhere else. There are no guest accounts, and inventing one for the
 * sake of a bowl of soup would lose more orders than it protects.
 */
export function OrderForm() {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { money } = useFormatters()

  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const [reference, setReference] = useState('')
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [requestedFor, setRequestedFor] = useState(() =>
    localInputValue(new Date(Date.now() + SOONEST_MS)),
  )

  const menu = useQuery({
    queryKey: ['services', 'orderable', locale],
    queryFn: () => servicesApi.orderable(locale),
  })

  const items = menu.data ?? []

  const order = useMutation({
    mutationFn: () =>
      servicesApi.placeOrder({
        bookingReference: reference.trim(),
        guestEmail: email.trim(),
        // The input is wall-clock local; the API takes an instant.
        requestedFor: new Date(requestedFor).toISOString(),
        lines: Object.entries(quantities)
          .filter(([, quantity]) => quantity > 0)
          .map(([serviceItemId, quantity]) => ({ serviceItemId, quantity })),
        guestNote: note.trim() || undefined,
      }),
  })

  const step = (item: ServiceItemDto, delta: number) =>
    setQuantities((current) => {
      const next = (current[item.id] ?? 0) + delta
      const capped = Math.min(Math.max(next, 0), item.maxQuantityPerOrder || 20)
      return { ...current, [item.id]: capped }
    })

  const chosen = useMemo(
    () =>
      items
        .map((item) => ({ item, quantity: quantities[item.id] ?? 0 }))
        .filter((line) => line.quantity > 0),
    [items, quantities],
  )

  // Only what has a confirmed price counts. An "ask us" item can still be ordered; it just
  // cannot be added up, and showing a total that quietly omits it would be worse than saying so.
  const priced = chosen.filter((line) => line.item.priceConfirmed && line.item.price != null)
  const total = priced.reduce((sum, line) => sum + (line.item.price ?? 0) * line.quantity, 0)
  const hasUnpriced = chosen.length > priced.length

  const ready =
    chosen.length > 0 && reference.trim().length > 2 && email.includes('@') && !!requestedFor

  if (order.isSuccess) {
    const placed = order.data
    return (
      <Card elevation={0} sx={{ border: 1, borderColor: 'primary.main', borderRadius: 4 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            🍲 {t('order.thanksTitle')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t('order.thanksBody')}
          </Typography>
          <Chip label={`${t('order.reference')}: ${placed.reference}`} color="primary" />
        </CardContent>
      </Card>
    )
  }

  if (!menu.isPending && items.length === 0) return null

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 4 }}>
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        <Typography variant="h3" sx={{ mb: 1 }}>
          {t('order.title')}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t('order.intro')}
        </Typography>

        {menu.isError && <ErrorState error={menu.error} onRetry={() => void menu.refetch()} />}

        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {items.map((item) => {
            const quantity = quantities[item.id] ?? 0
            return (
              <Stack
                key={item.id}
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{
                  p: 1.5,
                  border: 1,
                  borderColor: quantity > 0 ? 'primary.main' : 'divider',
                  borderRadius: 3,
                }}
              >
                {item.imageUrl && (
                  <Box
                    component="img"
                    src={item.imageUrl}
                    alt=""
                    loading="lazy"
                    sx={{ width: 56, height: 56, borderRadius: 2, objectFit: 'cover' }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {item.icon ? `${item.icon} ` : ''}
                    {item.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {item.priceConfirmed && item.price != null
                      ? money(item.price)
                      : t('services.askUs')}
                    {item.advanceNoticeMinutes > 0 &&
                      ` · ${t('order.noticeHours', {
                        count: Math.round(item.advanceNoticeMinutes / 60),
                      })}`}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <IconButton
                    size="small"
                    aria-label={t('order.fewer', { item: item.name })}
                    disabled={quantity === 0}
                    onClick={() => step(item, -1)}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>
                    {quantity}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={t('order.more', { item: item.name })}
                    onClick={() => step(item, 1)}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            )
          })}
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('order.bookingReference')}
              value={reference}
              onChange={(event) => setReference(event.target.value.toUpperCase())}
              helperText={t('order.bookingReferenceHelp')}
              fullWidth
              required
            />
            <TextField
              label={t('order.email')}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              helperText={t('order.emailHelp')}
              fullWidth
              required
            />
          </Stack>

          <TextField
            label={t('order.when')}
            type="datetime-local"
            value={requestedFor}
            onChange={(event) => setRequestedFor(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText={t('order.whenHelp')}
            required
          />

          <TextField
            label={t('order.note')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t('order.notePlaceholder')}
            multiline
            minRows={2}
          />

          {order.isError && <ErrorState error={order.error} />}

          {hasUnpriced && <Alert severity="info">{t('order.someAskUs')}</Alert>}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
          >
            <Box>
              <Typography variant="h5">
                {chosen.length === 0 ? t('order.nothingChosen') : money(total)}
              </Typography>
              {chosen.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t('order.itemCount', {
                    count: chosen.reduce((sum, line) => sum + line.quantity, 0),
                  })}
                </Typography>
              )}
            </Box>

            <Button
              variant="contained"
              size="large"
              disabled={!ready || order.isPending}
              onClick={() => order.mutate()}
            >
              {order.isPending ? t('order.sending') : t('order.send')}
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {t('order.noPaymentNote')}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  )
}
